import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export interface HealthDependency {
  name: string;
  type: string;
  status: 'UP' | 'DOWN';
  responseTime?: string;
}

export interface HealthDetails {
  status: 'UP' | 'DOWN';
  service: string;
  timestamp: string;
  uptime: number;
  dependencies: HealthDependency[];
}

export interface BasicHealth {
  status: 'UP' | 'DOWN';
  service: string;
  timestamp: string | number;
}

const healthKeys = {
  all: ['system-health'] as const,
  details: () => [...healthKeys.all, 'details'] as const,
  basic: () => [...healthKeys.all, 'basic'] as const,
};

// The health endpoints are NOT under /api/v1 — they're at the server root
// Use the API URL base without the /api/v1 suffix
function getHealthBaseUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
  // Strip /api/v1 suffix to get the root URL
  return apiUrl.replace(/\/api\/v1\/?$/, '');
}

async function fetchHealthDetails(): Promise<HealthDetails> {
  const baseUrl = getHealthBaseUrl();
  const response = await axios.get<HealthDetails>(`${baseUrl}/health/details`);
  return response.data;
}

async function fetchBasicHealth(): Promise<BasicHealth> {
  const baseUrl = getHealthBaseUrl();
  const response = await axios.get<BasicHealth>(`${baseUrl}/health`);
  return response.data;
}

/**
 * Fetch detailed health status including all dependencies.
 * Falls back to basic health check if /health/details is unavailable (e.g., separate port).
 */
export function useSystemHealth(refetchIntervalMs: number = 30000) {
  return useQuery<HealthDetails>({
    queryKey: healthKeys.details(),
    queryFn: async () => {
      try {
        return await fetchHealthDetails();
      } catch {
        // If /health/details fails (runs on separate port 8081), fall back to basic /health
        const basic = await fetchBasicHealth();
        return {
          status: basic.status as 'UP' | 'DOWN',
          service: basic.service,
          timestamp: typeof basic.timestamp === 'number'
            ? new Date(basic.timestamp).toISOString()
            : basic.timestamp,
          uptime: 0,
          dependencies: [],
        };
      }
    },
    refetchInterval: refetchIntervalMs,
    staleTime: 10 * 1000,
    retry: 1,
  });
}

export function useBasicHealth(refetchIntervalMs: number = 30000) {
  return useQuery<BasicHealth>({
    queryKey: healthKeys.basic(),
    queryFn: fetchBasicHealth,
    refetchInterval: refetchIntervalMs,
    staleTime: 10 * 1000,
    retry: 1,
  });
}
